import type { SVGProps } from "react";

import { cn } from "@/client/lib/utils";

/**
 * The Memoriam mark: a lowercase editorial "m" drawn as two joined arches with a
 * gold keystone above. Inline SVG so it inherits colour and needs no request.
 *
 * Master files live in `assets/brand/`; `bun run brand:render` regenerates the
 * favicon/app-icon set from them. Keep the geometry here in step with the masters.
 *
 * - `tile`: cream letterform on an Espresso tile (default; app icon, dark surfaces).
 * - `tile-light`: Espresso letterform on the warm-100 tile (brand slots on cream).
 * - `mono`: letterform only, in `currentColor`.
 *
 * Decorative by default (`aria-hidden`); pass `label` when the mark stands alone
 * as the only indication of the brand.
 */
export interface BrandMarkProps extends Omit<SVGProps<SVGSVGElement>, "aria-label"> {
  size?: number;
  variant?: "tile" | "tile-light" | "mono";
  label?: string;
}

const LETTERFORM = "M16 52 V34 a8 8 0 0 1 16 0 V52 M32 34 a8 8 0 0 1 16 0 V52";

export function BrandMark({ size = 32, variant = "tile", label, className, ...rest }: BrandMarkProps) {
  const tile = variant !== "mono";
  const tileFill = variant === "tile" ? "#3B322C" : "#F4EEE2";
  const letterform = variant === "tile" ? "#FBF7EF" : variant === "tile-light" ? "#3B322C" : "currentColor";
  const keystone = variant === "mono" ? "currentColor" : "#C9A863";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      className={cn("shrink-0", className)}
      {...rest}
    >
      {tile && <rect width="64" height="64" rx="14" fill={tileFill} />}
      <path d={LETTERFORM} fill="none" stroke={letterform} strokeWidth="8" />
      <circle cx="32" cy="14.5" r="3.75" fill={keystone} />
    </svg>
  );
}
